/**
 * Web Auth0 config for the counselor login bridge (RSW-301 / CPW-4.1).
 * Disabled by default — the bcrypt dev login stays the default path. Enable by
 * setting VITE_AUTH0_ENABLED=true plus the tenant values (tracked in RSW-76).
 *
 * Cloud Run injects per-environment values via `runtime-config.js` (RSWORKS_AUTH0_*)
 * so staging and prod can share one promoted web image with different SPA client IDs.
 */
const env = import.meta.env;

function readRuntimeAuth0() {
  if (typeof window === 'undefined') return null;
  const auth0 = window.__RSWORKS_RUNTIME_CONFIG__?.auth0;
  return auth0 && typeof auth0 === 'object' ? auth0 : null;
}

function resolveAuth0Field(runtimeKey, viteKey, fallback = '') {
  const runtime = readRuntimeAuth0();
  const runtimeValue = runtime?.[runtimeKey];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return String(runtimeValue);
  }
  const viteValue = env[viteKey];
  if (viteValue !== undefined && viteValue !== null && viteValue !== '') {
    return String(viteValue);
  }
  return fallback;
}

function resolveAuth0Enabled() {
  const runtime = readRuntimeAuth0();
  if (typeof runtime?.enabled === 'boolean') {
    return runtime.enabled;
  }
  return resolveAuth0Field('enabled', 'VITE_AUTH0_ENABLED') === 'true';
}

export const auth0Config = {
  enabled: resolveAuth0Enabled(),
  domain: resolveAuth0Field('domain', 'VITE_AUTH0_DOMAIN'),
  clientId: resolveAuth0Field('clientId', 'VITE_AUTH0_CLIENT_ID'),
  audience: resolveAuth0Field('audience', 'VITE_AUTH0_AUDIENCE'),
  // Default Auth0 connection for counselor SSO (RSW-756 passwordless email OTP).
  // Identifier-First needs an explicit `connection` or it picks the database
  // connection whenever both db + passwordless are enabled on the app.
  connection: resolveAuth0Field('connection', 'VITE_AUTH0_CONNECTION'),
  // Where Auth0 returns after Universal Login. Defaults to the app origin.
  redirectUri:
    resolveAuth0Field('redirectUri', 'VITE_AUTH0_REDIRECT_URI') ||
    (typeof window !== 'undefined' ? window.location.origin : ''),
};

const AUTH_PARAM_CONNECTIONS = {
  password: 'Username-Password-Authentication',
  google: 'google-oauth2',
  email: 'email',
};

/**
 * Resolve the Auth0 connection for counselor SSO. `?auth=password|google|email`
 * on the current URL overrides the configured default — the escape hatch that
 * keeps password-based smoke scripts working; anything else falls back to
 * config.connection.
 */
export function resolveLoginConnection(search, config = auth0Config) {
  const auth = new URLSearchParams(search || '').get('auth');
  if (auth && AUTH_PARAM_CONNECTIONS[auth]) {
    return AUTH_PARAM_CONNECTIONS[auth];
  }
  return config.connection || undefined;
}

/** True only when the flag is on AND the minimum tenant config is present. */
export function isAuth0Configured() {
  return Boolean(auth0Config.enabled && auth0Config.domain && auth0Config.clientId);
}
